import React, { useEffect, useState } from "react"
import styled from "@emotion/styled"
import { Flex, Box } from "@rebass/grid/emotion"
import { TextElementMen } from "@nutrafol/nutrafol-ui-kit/dist/TextElementMen"
import { ButtonWildMen } from "@nutrafol/nutrafol-ui-kit/dist/ButtonWildMen"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import { navigate } from "gatsby"
import CheckmarkIcon from "../../../../../../assets/icons/wild/checkmark-icon.svg"
import CoreUpsellRow from "./coreproduct-upsell-row"
import { useIsFirstRender } from "../../../../../../hooks/useIsFirstRender"
import LoadingAnimation from "../../../../../loading/loadinganimation"

const bearerToken = process.env.GATSBY_MAGENTO_API_BEARER_TOKEN
const envUrl = process.env.GATSBY_MAGENTO_URL
const DEVMODE = process.env.GATSBY_DEVMODE === "1"

const ADDED = `Item added to cart`
const COREINFO = `Core Product`
const ERRORMSG = `Error getting cart quote. Please proceed to cart.`
const ERRORMSG2 = `Error removing original product. Please proceed to cart.`
const ERRORMSG3 = `Error adding product to cart. Please proceed to cart.`
const HEADER1 = `Your foundation for hair growth.`
const HEADER2 = `Maximize your hair health potential.`
const LABELVIEW = "View Cart"
const LABELCHECKOUT = "Checkout"
const LABELADD = `Add to Subscription`
const LABELREMOVE = `Remove`
const PLANTITLE = `Hair Health Plan`
const PLANINFO = `Monthly Subscription`

const Wrap = styled(Box)``

const Rows = styled(Flex)`
  background: #fff;
  flex-direction: column;
  border-radius: 8px;

  hr {
    color: #c8d2d1;
  }

  .remove-button {
    text-transform: none !important;

    &.textLink {
      font-size: 14px !important;
      line-height: 19.6px !important;
      @media (min-width: 768px) {
        font-size: 16px !important;
        line-height: 24px !important;
      }
    }
  }
`
const ButtonContent = styled(Flex)`
  a,
  button {
    width: 100%;
    display: block;
    text-align: center;
    @media (min-width: 1024px) {
      max-width: 194px;
    }
  }
`
const AddedContent = styled(Flex)`
  align-items: center;
  gap: 8px;

  svg {
    width: 12px;
    path {
      fill: #008078;
    }
  }
`

const AddedRow = (
  <AddedContent>
    <Box width={[20]}>
      <CheckmarkIcon />
    </Box>
    <Box>
      <TextElementMen
        text={ADDED}
        className={`text-men-primary-default body--large-b`}
      />
    </Box>
  </AddedContent>
)

/*
 * NT2-18831
 * AB Test | W Core PDP + 1oz HS Upsell
 * LS useEffect operates with updated upsellAdded
 */

const CoreUpsellComponent = ({
  upsellOption,
  UPSELL_COMBO_PROPS,
  UPSELL_SOURCE,
  UPSELL_ITEM_PROPS,
  DESCRIPTION,
  REFS = null,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [upsellAdded, setUpsellAdded] = useState(false)
  const [message, setMessage] = useState(null)
  const isFirstRender = useIsFirstRender()
  const PRICE = upsellAdded ? UPSELL_COMBO_PROPS.price : upsellOption.price
  upsellOption.info = COREINFO

  useEffect(() => {
    if (isFirstRender) {
      return
    }
    segmentEvent(
      upsellAdded ? "Upsell Product Added" : "Upsell Product Removed",
      {
        products: [
          {
            billing_interval: UPSELL_COMBO_PROPS.billing_interval,
            image_url: UPSELL_COMBO_PROPS.image.url,
            name: UPSELL_COMBO_PROPS.name,
            price: UPSELL_COMBO_PROPS.price,
            product_category: UPSELL_COMBO_PROPS.product_category,
            product_gender: UPSELL_COMBO_PROPS.product_gender,
            purchase_type: UPSELL_COMBO_PROPS.purchase_type,
            shipping_interval: UPSELL_COMBO_PROPS.shipping_interval,
            sku: UPSELL_COMBO_PROPS.sku,
            subscription_type: UPSELL_COMBO_PROPS.subscription_type,
          },
        ],
        source: UPSELL_SOURCE,
      }
    )
  }, [upsellAdded])

  /**
   **
   * @returns promise
   */
  const addItem = () => {
    //LS upsellAdded here is not updated yet, be careful
    const sku = upsellAdded ? upsellOption.sku : UPSELL_COMBO_PROPS.sku
    const source = upsellAdded ? `mens_pdp` : `mens_core_active_cleanse_upgrade`
    const body = { skus: sku, purchase_source: source }

    fetch(`${envUrl}rest/V1/repack/add_to_cart/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((resJson) => {
        setIsSubmitted(false)
        //LS upsellAdded  updated here
        setUpsellAdded(!upsellAdded)
      })
      .catch((error) => {
        setIsSubmitted(false)
        setMessage(ERRORMSG3)
        console.error("Error:", error)
      })
  }

  const handleClick = () => {
    if (isSubmitted) {
      return
    }
    setMessage(null)
    setIsSubmitted(true)
    /*
     * for cart development locally
     * https://nutrafol.atlassian.net/wiki/spaces/NS/pages/1787920385/Cart+-+working+locally
     */
    let url = `${envUrl}rest/V1/nutrafol-carts/`
    if (DEVMODE) {
      //url = `${envUrl}rest/V1/nutrafol-carts/TXOqM2FbR4Y2fDGyTdo3PTEYMkwtzaXk`
    }

    fetch(url, {
      method: "GET",
    })
      .then((res) => res.json())
      .then(async (resJson) => {
        if (typeof resJson !== "object") {
          const cartData = JSON.parse(resJson)
          if (typeof cartData === "object" && cartData.items) {
            //swap skus: delete first then add another one
            const cartItem = upsellAdded
              ? cartData.items?.[UPSELL_COMBO_PROPS.sku]
              : cartData.items?.[upsellOption.sku]

            const quote = cartItem?.quote
            const cartId = cartData?.totals?.cart_id
            const headers = {
              Accept: "application/json",
              "Content-Type": "application/json; charset=UTF-8",
              Authorization: `Bearer ${bearerToken}`,
            }

            //console.log("quote", quote)

            if (cartItem && quote && cartId) {
              const url = `${envUrl}rest/V1/carts/${cartId}/items/${quote?.quote_item_id}`
              try {
                if (quote.qty === 1) {
                  await fetch(url, {
                    method: "DELETE",
                    withCredentials: true,
                    headers,
                  })
                } else {
                  let updatedBody = {
                    cartItem: {
                      quoteId: Number(cartId),
                      qty: quote.qty - 1,
                    },
                  }
                  await fetch(url, {
                    method: "PUT",
                    withCredentials: true,
                    headers,
                    body: JSON.stringify(updatedBody),
                  })
                }

                if (upsellAdded) {
                  //console.log("deleted upsell, adding one month...")
                } else {
                  //console.log("deleted one month, adding upsell...")
                }
                addItem()
              } catch (error) {
                setMessage(ERRORMSG2)
                console.error("delete error: ", error)
              }
            } else {
              console.error("nothing to delete, quitting...")
              setMessage(ERRORMSG2)
              setIsSubmitted(false)
            }
          } else {
            setMessage(cartData)
            setIsSubmitted(false)
          }
        } else {
          setMessage(ERRORMSG)
          setIsSubmitted(false)
        }
      })
      .catch((error) => {
        setMessage(ERRORMSG)
        setIsSubmitted(false)
        console.warn("Fetch error: ", error)
      })
  }

  const handleCheckout = (
    text = LABELVIEW,
    destination = "/checkout/cart/",
    outside = false
  ) => {
    segmentEvent("Marketing CTA Clicked", {
      destination,
      text,
      type: "button",
    })
    setTimeout(() => {
      if (outside) {
        window.location = envUrl + destination
      } else {
        navigate(destination)
      }
    }, 500)
  }

  return (
    <Wrap>
      {upsellAdded && isSubmitted ? <LoadingAnimation /> : null}
      <Rows py={16} px={[16, null, null, 32]}>
        <Box pb={[24]}>
          <TextElementMen
            text={HEADER1}
            className={`text-men-secondary-secondary title--small uppercase`}
          />
        </Box>
        <CoreUpsellRow item={upsellOption}></CoreUpsellRow>
        <Box py={[8]}>{AddedRow}</Box>
        <hr />
        <Box py={[16]}>
          <TextElementMen text={HEADER2} className={`mono--small uppercase`} />
        </Box>
        <CoreUpsellRow item={UPSELL_ITEM_PROPS}></CoreUpsellRow>
        <Box py={[16]}>
          <TextElementMen
            text={DESCRIPTION}
            className={`text-men-secondary-secondary body--regular `}
          />
        </Box>
        {message ? (
          <Box py={[24]}>
            <TextElementMen
              text={message}
              className={`text-men-functional-error body--large-b`}
            />
          </Box>
        ) : null}
        {upsellAdded ? (
          <Flex py={[16]}>
            <Box width={["auto"]} flex={1}>
              {AddedRow}
            </Box>
            <Box width={[75]} className={`text-right`}>
              <ButtonWildMen
                textLink
                className={`remove-button`}
                onClick={handleClick}
                label={LABELREMOVE}
              />
            </Box>
          </Flex>
        ) : (
          <Box>
            <ButtonWildMen
              style={{ width: "100%" }}
              secondary
              className={`${isSubmitted ? "disabled" : ""}`}
              loading={isSubmitted ? "loading" : null}
              onClick={handleClick}
              label={LABELADD}
            />
          </Box>
        )}
        <Box pt={[16]} pb={[16]}>
          <hr />
        </Box>
        <Flex width={[1]}>
          <Box>
            <TextElementMen
              text={PLANTITLE}
              className={`text-men-secondary-secondary body--large-b`}
            />
          </Box>
          <Box ml={["auto"]}>
            <TextElementMen
              text={`$${PRICE}/mo`}
              className={`text-men-secondary-secondary body--large-b`}
            />
          </Box>
        </Flex>
        <Flex width={[1]} pt={["4px"]}>
          <TextElementMen
            text={PLANINFO}
            className={`text-men-base-alt-alt body--regular`}
          />
        </Flex>
      </Rows>
      {REFS ? (
        <Box>
          <TextElementMen
            text={REFS}
            className={`body--small text-center mt-2 text-master-base-alt-disabled`}
          />
        </Box>
      ) : null}
      <ButtonContent
        flexWrap="wrap"
        width="100%"
        mt={[32]}
        justifyContent={`center`}
      >
        <Box width={[0.5, 0.5, 0.5, 194]} pr={1}>
          <ButtonWildMen
            secondary
            onClick={() => {
              handleCheckout()
            }}
            label={LABELVIEW}
          />
        </Box>
        <Box width={[0.5, 0.5, 0.5, 194]} pl={1}>
          <ButtonWildMen
            primary
            arrowRight
            label={LABELCHECKOUT}
            onClick={() => {
              handleCheckout(LABELCHECKOUT, `checkout/index/`, true)
            }}
          />
        </Box>
      </ButtonContent>
    </Wrap>
  )
}

export default CoreUpsellComponent
